import { MouseEvent, useCallback, useMemo, SetStateAction } from 'react';

import { useLaunch } from '@/hooks/shared/useLaunch';
import { useAddToLibraryMutation, useRemoveFromLibraryMutation } from '@/redux/api/client/library';
import useToast from '@/hooks/useToast';
import { SnippetActions } from '../../Snippet/Snippet';
import { SearchableType } from '../searchableTypeToSnippetVariant';
import { ActionType } from './useEcommData';
import { SearchModalPanelState } from './useSearchModalPanelState';
import { useDispatch } from 'react-redux';
import { ActionStatus } from '../constants';
import { AnalyticsContext } from '@/analytics/constants';
import {
  trackAddWishListEvent,
  trackRemoveWishListEvent,
  trackSelectItem
} from '@/analytics/ecommerce';
import { ItemAnalyticsModel, ItemListAnalyticsModel } from '@/@types/client-api';
import { setOptimisticToggleForLibraryFavoriteItemUpdate } from '@/redux/slices/searchSlice';
import { NotifyPanelItems } from '@/redux/slices/pdpSlice';
import { isNativeNotifyMeSource } from '../../PDP/ECommBox/notifyMeHelpers';
import { launchNotifyMePopup } from '@/utils/helpers';
import useResolveNotifyMeItems from '@/hooks/PDP/useResolveNotifyMeItems';

const useSnippetActions = (
  pk: string,
  url: string,
  title: string,
  context: AnalyticsContext,
  searchableType: SearchableType | null,
  modalPanelState: SearchModalPanelState,
  hasMultipleRecordings: boolean,
  guidValue: string | null,
  setActionStatus?: React.Dispatch<SetStateAction<ActionStatus>>,
  refetchData?: (deletedCount?: number) => void,
  launchUrl?: string, // only set if already purchased
  itemAnalyticsModel?: ItemAnalyticsModel,
  itemListAnalytics?: ItemListAnalyticsModel,
  listId?: string,
  listName?: string,
  notifyMeUrl?: string | null,
  notifyItems?: NotifyPanelItems,
  notifyMeCode?: string
): SnippetActions => {
  const { ecommPanel, materialsModal, addToCalendarModal } = modalPanelState;

  const itemListAnalyticsModel = useMemo<ItemListAnalyticsModel>(
    () => ({
      listId:
        itemListAnalytics?.listId ||
        listId ||
        (context == 'search'
          ? window?.location?.pathname + window?.location?.search
          : 'Library_Saved'),
      listName:
        itemListAnalytics?.listName ||
        listName ||
        (context == 'search' ? 'SearchResults' : 'MyLibrary')
    }),
    [context, itemListAnalytics, listId, listName]
  );
  const analyticsListId = itemListAnalytics?.listId || listId;
  const analyticsListName = itemListAnalytics?.listName || listName;

  const { launch } = useLaunch(context, undefined, itemListAnalyticsModel);
  const [addToLibrary] = useAddToLibraryMutation();
  const [removeFromLibrary] = useRemoveFromLibraryMutation();
  const { showSuccessMessage, showFailMessage } = useToast();
  const dispatch = useDispatch();
  const resolveNotifyMeItems = useResolveNotifyMeItems();

  const handleAddToLibrary = useCallback(async () => {
    setActionStatus?.('loading');
    try {
      if (context && itemAnalyticsModel) {
        trackSelectItem(itemAnalyticsModel, context, analyticsListId, analyticsListName);
        trackAddWishListEvent(itemAnalyticsModel, context, itemListAnalyticsModel);
      }
      await addToLibrary(pk).unwrap();
      setActionStatus?.('idle');
      showSuccessMessage('Saved item to library');
      dispatch(setOptimisticToggleForLibraryFavoriteItemUpdate([pk]));
    } catch (addToLibraryError) {
      setActionStatus?.('error');
      showFailMessage('Error while saving item to library');
    }
  }, [
    addToLibrary,
    context,
    dispatch,
    itemAnalyticsModel,
    analyticsListId,
    analyticsListName,
    itemListAnalyticsModel,
    pk,
    setActionStatus,
    showFailMessage,
    showSuccessMessage
  ]);

  const handleRemoveFromLibrary = useCallback(
    async (variantPks?: string[]) => {
      setActionStatus?.('loading');
      if (variantPks?.length) {
        try {
          const deletedItems = await Promise.all(
            variantPks.map(async variantPk => {
              trackRemoveWishListEvent(itemAnalyticsModel, context, false, itemListAnalyticsModel);
              return removeFromLibrary(variantPk).unwrap();
            })
          );
          setActionStatus?.('idle');
          showSuccessMessage('Removed item from library');
          dispatch(setOptimisticToggleForLibraryFavoriteItemUpdate(variantPks));
          refetchData?.(deletedItems.length);
        } catch {
          setActionStatus?.('error');
          showFailMessage('Error while removing item from library');
        }
      }
    },
    [
      removeFromLibrary,
      showSuccessMessage,
      showFailMessage,
      setActionStatus,
      refetchData,
      dispatch,
      context,
      itemAnalyticsModel,
      itemListAnalyticsModel
    ]
  );

  const handleShowEcommPanel = useCallback(
    (action: ActionType, resolvedNotifyItems: NotifyPanelItems | undefined = notifyItems) => {
      ecommPanel.show({
        pk,
        guidValue,
        url,
        title,
        searchableType,
        action,
        notifyMeUrl: notifyMeUrl ?? undefined,
        itemAnalyticsModel,
        context,
        listId,
        listName,
        notifyItems: resolvedNotifyItems,
        notifyMeCode: notifyMeCode || pk
      });
    },
    [
      context,
      ecommPanel,
      guidValue,
      itemAnalyticsModel,
      listId,
      listName,
      notifyItems,
      notifyMeUrl,
      notifyMeCode,
      pk,
      searchableType,
      title,
      url
    ]
  );

  const handleShowMaterialsModal = useCallback(
    (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      e?.preventDefault();
      materialsModal.show({ pk, title });
    },
    [pk, materialsModal, title]
  );

  const handleShowAddToCalendarModal = useCallback(
    (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      e?.preventDefault();
      addToCalendarModal.show({ pk });
    },
    [pk, addToCalendarModal]
  );

  const handleSeeSchedule = useCallback(
    (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      e?.preventDefault();
      window.open(`${url}#schedule`);
    },
    [url]
  );

  const actions = useMemo(
    () => ({
      launch: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e?.preventDefault();
        if (hasMultipleRecordings) {
          handleShowEcommPanel('StartWatching');
        } else {
          launch({
            code: pk,
            isLiveUnregLaunch: false,
            alreadyPurchasedLaunchUrl: launchUrl,
            itemAnalyticsModel,
            listId,
            listName
          }).then(() => refetchData?.());
        }
      },
      addToLibrary: handleAddToLibrary,
      removeFromLibrary: (variantPks?: string[]) => handleRemoveFromLibrary(variantPks),
      addToCalendar: handleShowAddToCalendarModal,
      accessMaterials: handleShowMaterialsModal,
      buy: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e?.preventDefault();
        handleShowEcommPanel('BuyNow');
      },
      seeSchedule: handleSeeSchedule,
      register: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e?.preventDefault();
        handleShowEcommPanel('Register');
      },
      viewRegistration: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e?.preventDefault();
        handleShowEcommPanel('ViewRegistration');
      },
      changeOrCancelRegistration: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e?.preventDefault();
        handleShowEcommPanel('ChangeOrCancelRegistration');
      },
      notifyMe: async () => {
        setActionStatus?.('loading');
        const resolvedNotifyItems = await resolveNotifyMeItems(url, notifyItems);
        const legacyUrl = resolvedNotifyItems?.url ?? notifyMeUrl;

        if (isNativeNotifyMeSource(resolvedNotifyItems?.source)) {
          setActionStatus?.('idle');
          handleShowEcommPanel('NotifyMe', resolvedNotifyItems);
          return;
        }

        setActionStatus?.('idle');
        if (legacyUrl) {
          launchNotifyMePopup(legacyUrl);
        }
      }
    }),

    [
      handleAddToLibrary,
      handleShowAddToCalendarModal,
      handleShowMaterialsModal,
      handleSeeSchedule,
      hasMultipleRecordings,
      handleShowEcommPanel,
      launch,
      pk,
      launchUrl,
      itemAnalyticsModel,
      listId,
      listName,
      refetchData,
      handleRemoveFromLibrary,
      notifyItems,
      notifyMeUrl,
      resolveNotifyMeItems,
      setActionStatus,
      url
    ]
  );

  return actions;
};

export default useSnippetActions;
